import type { MemberDto } from '@aawas/contracts';
import clsx from 'clsx';
import { AlertTriangle, DoorOpen, KeyRound, Search, ShieldCheck, Ticket, Upload, UserPlus, Users } from 'lucide-react';
import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { issueLoginCode, useAddMember, useBulkMembers, useBulkUnits, useCommunity, useMembers, useNotices, useTickets, useUnits, useUpdateCommunity, useUpdateMember, useVisits, useCreateNotice, useDeleteNotice } from '../lib/queries';
import { useSession } from '../lib/session';
import { Badge, Button, Card, Empty, ErrorNote, Field, Input, PageHeader, Segmented, Select, Sheet, Spinner, Textarea, fmtDateTime } from '../ui';
import { copyText } from '../lib/clipboard';
import { parseWholeNumber } from '../lib/numeric';
import { prepareImport } from '../lib/residentImport';
import { useToast } from '../ui/toast';
import { TicketRow } from './complaints';

// ───────────── overview ─────────────

function Stat({ icon: Icon, label, value, tone = 'slate', to }: { icon: typeof Users; label: string; value: number | string; tone?: 'slate' | 'red' | 'green' | 'amber'; to?: string }) {
  const c = { slate: 'text-slate-900', red: 'text-rose-700', green: 'text-emerald-700', amber: 'text-amber-700' }[tone];
  const body = (
    <Card className="!p-4">
      <Icon className="mb-2 h-5 w-5 text-slate-400" aria-hidden />
      <div className={clsx('text-3xl font-bold tabular-nums', c)}>{value}</div>
      <div className="text-sm text-slate-600">{label}</div>
    </Card>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

export function AdminOverview() {
  const { current } = useSession();
  const tickets = useTickets({ limit: 100 }, 30_000);
  const visits = useVisits({ limit: 100 }, 15_000);
  const members = useMembers();
  const all = tickets.data?.items ?? [];
  const open = all.filter((t) => t.status !== 'closed' && t.status !== 'resolved');
  const overdue = open.filter((t) => t.slaBreached);
  const today = (visits.data?.items ?? []).filter((v) => Date.now() - new Date(v.createdAt).getTime() < 86_400_000);
  const inside = today.filter((v) => v.status === 'entered').length;
  const noAnswer = today.filter((v) => v.status === 'no_response').length;
  const residents = (members.data ?? []).filter((m) => ['owner', 'tenant', 'family'].includes(m.role) && m.status === 'active').length;
  const rated = all.filter((t) => t.rating);
  const avg = rated.length ? (rated.reduce((s, t) => s + (t.rating ?? 0), 0) / rated.length).toFixed(1) : '–';

  return (
    <div className="space-y-5">
      <PageHeader title="Overview" subtitle={current.communityName} />
      <div className="grid grid-cols-2 gap-3">
        <Stat icon={Ticket} label="Open complaints" value={open.length} to="/complaints" />
        <Stat icon={AlertTriangle} label="Past their target" value={overdue.length} tone={overdue.length ? 'red' : 'green'} to="/complaints" />
        <Stat icon={DoorOpen} label="Visitors today" value={today.length} to="/visitors" />
        <Stat icon={ShieldCheck} label="Inside now" value={inside} />
        <Stat icon={Users} label="Residents on the app" value={residents} to="/members" />
        <Stat icon={AlertTriangle} label="Unanswered visitors" value={noAnswer} tone={noAnswer ? 'amber' : 'green'} />
      </div>
      <p className="text-sm text-slate-600">Average complaint rating: <b>{avg}</b>{rated.length ? ` from ${rated.length} rating${rated.length > 1 ? 's' : ''}` : ''}</p>
      {overdue.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-sm font-bold uppercase tracking-wide text-rose-800">Needs attention</h2>
          {overdue.slice(0, 5).map((t) => <TicketRow key={t.id} t={t} showUnit />)}
        </section>
      )}
      {tickets.isLoading && <Spinner />}
    </div>
  );
}

// ───────────── members ─────────────

const ROLE_FILTERS = [
  { value: 'residents', label: 'Residents' },
  { value: 'guard', label: 'Security' },
  { value: 'staff', label: 'Staff' },
  { value: 'admin', label: 'Committee' },
] as const;
type RoleFilter = (typeof ROLE_FILTERS)[number]['value'];

export function MembersScreen() {
  const toast = useToast();
  const [filter, setFilter] = useState<RoleFilter>('residents');
  const [q, setQ] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [unitsOpen, setUnitsOpen] = useState(false);
  const [code, setCode] = useState<{ code: string; phone: string; name: string } | null>(null);
  const members = useMembers();
  const update = useUpdateMember();

  const list = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (members.data ?? []).filter((m) => {
      const inGroup = filter === 'residents' ? ['owner', 'tenant', 'family'].includes(m.role) : m.role === filter;
      return inGroup && (!t || `${m.name ?? ''} ${m.phone} ${m.unitLabel ?? ''}`.toLowerCase().includes(t));
    });
  }, [members.data, filter, q]);

  const [issuing, setIssuing] = useState<string | null>(null);
  async function giveCode(m: MemberDto) {
    if (issuing) return; // a double-click must not create two codes
    setIssuing(m.membershipId);
    try {
      const r = await issueLoginCode(m.membershipId);
      setCode({ code: r.code, phone: r.phone, name: m.name ?? r.phone });
    } catch (e) {
      toast.error(e);
    } finally {
      setIssuing(null);
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader title="People" subtitle={`${members.data?.length ?? 0} registered`} action={<Button size="sm" onClick={() => setAddOpen(true)}><UserPlus className="h-4 w-4" /> Add</Button>} />
      <Segmented value={filter} onChange={setFilter} options={ROLE_FILTERS.map((f) => ({ ...f }))} />
      <div className="relative">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" aria-hidden />
        <Input className="pl-11" placeholder="Search name, phone or flat" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search people" />
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="secondary" onClick={() => setUnitsOpen(true)}>Set up flats</Button>
        <Button size="sm" variant="secondary" onClick={() => setImportOpen(true)}><Upload className="h-4 w-4" /> Import residents</Button>
      </div>

      {members.isLoading ? <Spinner /> : members.error ? <ErrorNote error={members.error} retry={() => members.refetch()} /> : list.length === 0 ? (
        <Empty icon={<Users className="h-8 w-8" />} title="Nobody here yet" hint="Set up your flats, then import residents from your existing list or add them one by one." />
      ) : (
        <div className="space-y-2">
          {list.map((m) => (
            <Card key={m.membershipId} className={clsx('!p-3', m.status !== 'active' && 'opacity-60')}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate font-semibold">{m.name ?? '—'}</div>
                  <div className="text-sm text-slate-600">{m.phone}{m.unitLabel ? ` · Flat ${m.unitLabel}` : ''}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge tone={m.role === 'admin' ? 'purple' : m.role === 'guard' ? 'blue' : m.role === 'staff' ? 'amber' : 'gray'}>{m.title ?? m.role}</Badge>
                  {m.status !== 'active' && <Badge tone="red">{m.status}</Badge>}
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button size="sm" variant="secondary" loading={issuing === m.membershipId} disabled={!!issuing} onClick={() => giveCode(m)}><KeyRound className="h-4 w-4" /> Login code</Button>
                {m.status === 'active' ? (
                  <Button size="sm" variant="ghost" className="!text-rose-700" onClick={() => window.confirm(`Suspend ${m.name ?? m.phone}? They will be signed out of this society.`) && update.mutate({ id: m.membershipId, patch: { status: 'suspended' } }, { onError: (e) => toast.error(e) })}>Suspend</Button>
                ) : (
                  <Button size="sm" variant="ghost" onClick={() => update.mutate({ id: m.membershipId, patch: { status: 'active' } }, { onError: (e) => toast.error(e) })}>Reactivate</Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <AddMemberSheet open={addOpen} onClose={() => setAddOpen(false)} />
      <ImportSheet open={importOpen} onClose={() => setImportOpen(false)} />
      <UnitsSheet open={unitsOpen} onClose={() => setUnitsOpen(false)} />
      <Sheet open={!!code} onClose={() => setCode(null)} title="One-time login code">
        {code && (
          <div className="space-y-4 text-center">
            <p className="text-slate-700">Give this code to <b>{code.name}</b> ({code.phone}). It works once and expires in 24 hours.</p>
            <div className="select-all rounded-2xl bg-brand-50 py-5 font-mono text-5xl font-bold tracking-[0.3em] text-brand-700" aria-label={`Login code ${code.code.split('').join(' ')}`}>{code.code}</div>
            <p className="text-sm text-slate-600">They open the app, enter their mobile number, then this code.</p>
            <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-900">This code stays the same until you close this window. Pressing <b>Login code</b> again makes a <b>new</b> code and the old one stops working.</p>
            <Button
              size="lg"
              className="w-full"
              onClick={async () => {
                const ok = await copyText(`Your Aawas login code: ${code.code} (valid 24 hours). Open the app, enter your mobile number, then this code.`);
                if (ok) toast.success('Copied. Paste it into WhatsApp or SMS.');
                else toast.show({ title: 'Could not copy automatically', body: 'Press and hold the code above to copy it.', tone: 'alert' });
              }}
            >
              Copy message
            </Button>
          </div>
        )}
      </Sheet>
    </div>
  );
}

function AddMemberSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const units = useUnits();
  const add = useAddMember();
  const [role, setRole] = useState('owner');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [unitId, setUnitId] = useState('');
  const [title, setTitle] = useState('');
  const resident = ['owner', 'tenant', 'family'].includes(role);

  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      await add.mutateAsync({ role, name: name.trim(), phone: phone.trim(), ...(resident && { unitId }), ...(title.trim() && { title: title.trim() }) });
      toast.success(`${name.trim()} added`);
      setName(''); setPhone(''); setTitle(''); onClose();
    } catch (err) { toast.error(err); }
  }
  return (
    <Sheet open={open} onClose={onClose} title="Add a person">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Role">
          <Select value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="owner">Flat owner</option><option value="tenant">Tenant</option><option value="family">Family member</option>
            <option value="guard">Security guard</option><option value="staff">Facility staff</option><option value="admin">Committee member</option>
          </Select>
        </Field>
        {resident && (
          <Field label="Flat">
            <Select value={unitId} onChange={(e) => setUnitId(e.target.value)} required>
              <option value="">Choose a flat…</option>
              {units.data?.map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
            </Select>
          </Field>
        )}
        <Field label="Full name"><Input value={name} onChange={(e) => setName(e.target.value)} required maxLength={80} /></Field>
        <Field label="Mobile number"><Input inputMode="numeric" value={phone} onChange={(e) => setPhone(e.target.value)} required placeholder="98765 43210" /></Field>
        {role === 'admin' && <Field label="Position (optional)"><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="President, Secretary, Treasurer…" /></Field>}
        <Button type="submit" size="lg" className="w-full" loading={add.isPending} disabled={!name.trim() || !phone.trim() || (resident && !unitId)}>Add</Button>
      </form>
    </Sheet>
  );
}

function ImportSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const bulk = useBulkMembers();
  const [text, setText] = useState('');
  const [result, setResult] = useState<{ created: number; skipped: Array<{ row: number; reason: string }> } | null>(null);

  // Every row is checked here first, so one typo never blocks the rest and problems are named by spreadsheet line.
  const { rows, problems } = useMemo(() => prepareImport(text), [text]);
  const [sentProblems, setSentProblems] = useState<typeof problems>([]);

  async function run() {
    try {
      const res = await bulk.mutateAsync(rows.map(({ block, unit, name, phone, role }) => ({ block, unit, name, phone, role })));
      // The server numbers rows within what we sent; translate back to the spreadsheet's own line numbers.
      setResult({ created: res.created, skipped: res.skipped.map((s) => ({ row: rows[s.row - 1]?.line ?? s.row, reason: s.reason })) });
      setSentProblems(problems);
    } catch (e) { toast.error(e); }
  }

  return (
    <Sheet open={open} onClose={() => { setResult(null); onClose(); }} title="Import residents">
      {result ? (
        <div className="space-y-3">
          <div className="rounded-xl bg-emerald-50 p-4 text-emerald-900"><b>{result.created}</b> residents added.</div>
          {(result.skipped.length > 0 || sentProblems.length > 0) && (
            <div className="rounded-xl bg-amber-50 p-4 text-sm text-amber-900">
              <div className="mb-1 font-semibold">{result.skipped.length + sentProblems.length} rows were not imported. Fix these lines in your spreadsheet and import them again:</div>
              <ul className="max-h-48 list-disc overflow-y-auto pl-5">
                {[...sentProblems.map((p) => ({ line: p.line, text: `${p.what}: ${p.why}` })), ...result.skipped.map((s) => ({ line: s.row, text: s.reason }))]
                  .sort((a, b) => a.line - b.line)
                  .map((p) => <li key={`${p.line}-${p.text}`}>Line {p.line}: {p.text}</li>)}
              </ul>
            </div>
          )}
          <Button size="lg" className="w-full" onClick={() => { setResult(null); setSentProblems([]); setText(''); onClose(); }}>Done</Button>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-slate-700">Paste rows from your spreadsheet with these columns: <b>Block, Flat, Name, Mobile, Role</b> (owner or tenant). Set up your flats first.</p>
          <Textarea rows={8} className="font-mono text-sm" value={text} onChange={(e) => setText(e.target.value)} placeholder={'A, 101, Asha Nair, 9876543210, owner\nA, 102, Bala Menon, 9876543211, tenant'} aria-label="Resident rows" />
          <div className="flex items-center justify-between text-sm text-slate-600">
            <span><b className="text-emerald-700">{rows.length}</b> ready{problems.length > 0 && <>, <b className="text-rose-700">{problems.length}</b> with problems</>}</span>
            <label className="cursor-pointer font-semibold text-brand-700">
              Upload .csv
              <input type="file" accept=".csv,text/csv,text/plain" hidden onChange={async (e) => { const f = e.target.files?.[0]; if (f) setText(await f.text()); e.target.value = ''; }} />
            </label>
          </div>
          {problems.length > 0 && (
            <div role="alert" className="rounded-xl bg-rose-50 p-3 text-sm text-rose-900">
              <div className="mb-1 font-semibold">These rows will be skipped. You can still import the rest.</div>
              <ul className="max-h-32 list-disc overflow-y-auto pl-5">{problems.map((p) => <li key={p.line}>Line {p.line} ({p.what}): {p.why}</li>)}</ul>
            </div>
          )}
          <Button size="lg" className="w-full" loading={bulk.isPending} disabled={rows.length === 0} onClick={run}>Import {rows.length || ''} residents</Button>
        </div>
      )}
    </Sheet>
  );
}

function UnitsSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const bulk = useBulkUnits();
  const units = useUnits();
  const [block, setBlock] = useState('');
  // Kept as text so the boxes can be cleared and retyped; parsed below.
  const [floorsText, setFloorsText] = useState('5');
  const [perFloorText, setPerFloorText] = useState('4');
  const [areaText, setAreaText] = useState('');
  const area = areaText.trim() === '' ? undefined : Number(areaText);
  const areaBad = area !== undefined && !(area > 0 && area <= 1_000_000 && Math.round(area * 100) / 100 === area);
  const floors = parseWholeNumber(floorsText, 1, 60);
  const perFloor = parseWholeNumber(perFloorText, 1, 20);
  const generated = useMemo(() => {
    const out: string[] = [];
    if (floors === null || perFloor === null) return out;
    for (let f = 1; f <= floors; f++) for (let n = 1; n <= perFloor; n++) out.push(`${f}${String(n).padStart(2, '0')}`);
    return out;
  }, [floors, perFloor]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      const r = await bulk.mutateAsync([{ block: block.trim(), units: generated, ...(area !== undefined && { areaSqft: area }) }]);
      toast.success(r.units === 0 ? 'Those flats already exist, so nothing was added' : `${r.units} new flat${r.units === 1 ? '' : 's'} added to block ${block.trim()}`);
      setBlock('');
      setAreaText('');
    } catch (err) { toast.error(err); }
  }
  return (
    <Sheet open={open} onClose={onClose} title="Set up flats">
      <div className="space-y-4">
        <p className="text-sm text-slate-700">{units.data?.length ?? 0} flats set up. Add a tower or block; flats are numbered by floor (101, 102 … 501, 502).</p>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Block / tower name"><Input value={block} onChange={(e) => setBlock(e.target.value)} required maxLength={40} placeholder="A" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Floors" hint="1 to 60"><Input inputMode="numeric" pattern="[0-9]*" value={floorsText} onChange={(e) => setFloorsText(e.target.value)} onFocus={(e) => e.currentTarget.select()} /></Field>
            <Field label="Flats per floor" hint="1 to 20"><Input inputMode="numeric" pattern="[0-9]*" value={perFloorText} onChange={(e) => setPerFloorText(e.target.value)} onFocus={(e) => e.currentTarget.select()} /></Field>
          </div>
          <Field label="Size of each flat (sq ft)" hint="Optional. Needed if maintenance is charged per sq ft. Flats of different sizes can be edited later in Finance → Setup." error={areaBad ? 'Enter a number above zero' : undefined}><Input inputMode="decimal" value={areaText} onChange={(e) => setAreaText(e.target.value)} placeholder="e.g. 1200" /></Field>
          {generated.length === 0 ? (
            <p className="text-sm text-amber-800">Enter the number of floors (1–60) and flats per floor (1–20).</p>
          ) : (
            <p className="text-sm text-slate-600">This will create {generated.length} flat{generated.length === 1 ? '' : 's'}: {generated.length <= 6 ? generated.join(', ') : `${generated.slice(0, 4).join(', ')} … ${generated.at(-1)}`}. Existing flats are left alone.</p>
          )}
          <Button type="submit" size="lg" className="w-full" loading={bulk.isPending} disabled={!block.trim() || generated.length === 0 || areaBad}>Create flats</Button>
        </form>
      </div>
    </Sheet>
  );
}

// ───────────── notices (admin can post) ─────────────

export function NoticesScreen() {
  const { current } = useSession();
  const toast = useToast();
  const isAdmin = current.role === 'admin';
  const q = useNotices(isAdmin);
  const del = useDeleteNotice();
  const [open, setOpen] = useState(false);

  return (
    <div className="space-y-4">
      <PageHeader title="Notice board" action={isAdmin ? <Button size="sm" onClick={() => setOpen(true)}>Post notice</Button> : undefined} />
      {q.isLoading ? <Spinner /> : q.error ? <ErrorNote error={q.error} retry={() => q.refetch()} /> : (q.data ?? []).length === 0 ? (
        <Empty title="No notices" hint="Announcements from your committee appear here." />
      ) : (
        <div className="space-y-3">
          {q.data!.map((n) => (
            <Card key={n.id} className={clsx(n.pinned && 'ring-2 ring-brand-200')}>
              <div className="flex items-start justify-between gap-2">
                <h2 className="font-semibold">{n.pinned ? '📌 ' : ''}{n.title}</h2>
                <Badge tone={n.category === 'emergency' ? 'red' : n.category === 'maintenance' ? 'amber' : 'blue'}>{n.category}</Badge>
              </div>
              <p className="mt-2 whitespace-pre-wrap text-sm text-slate-800">{n.body}</p>
              <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                <span>{n.authorName ?? 'Committee'} · {fmtDateTime(n.publishedAt)}{n.audience !== 'all' ? ` · ${n.audience} only` : ''}</span>
                {isAdmin && <button className="font-semibold text-rose-700" onClick={() => window.confirm('Delete this notice?') && del.mutate(n.id, { onError: (e) => toast.error(e) })}>Delete</button>}
              </div>
            </Card>
          ))}
        </div>
      )}
      <NewNoticeSheet open={open} onClose={() => setOpen(false)} />
    </div>
  );
}

function NewNoticeSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const create = useCreateNotice();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [category, setCategory] = useState('general');
  const [audience, setAudience] = useState('all');
  const [pinned, setPinned] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      await create.mutateAsync({ title: title.trim(), body: body.trim(), category, audience, pinned });
      toast.success('Notice posted. Residents have been notified.');
      setTitle(''); setBody(''); setPinned(false); onClose();
    } catch (err) { toast.error(err); }
  }
  return (
    <Sheet open={open} onClose={onClose} title="Post a notice">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Title"><Input value={title} onChange={(e) => setTitle(e.target.value)} required minLength={3} maxLength={140} autoFocus /></Field>
        <Field label="Message"><Textarea value={body} onChange={(e) => setBody(e.target.value)} required minLength={3} maxLength={5000} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Type"><Select value={category} onChange={(e) => setCategory(e.target.value)}><option value="general">General</option><option value="maintenance">Maintenance</option><option value="event">Event</option><option value="security">Security</option><option value="meeting">Meeting</option><option value="emergency">Emergency</option></Select></Field>
          <Field label="Who sees it"><Select value={audience} onChange={(e) => setAudience(e.target.value)}><option value="all">Everyone</option><option value="owners">Owners only</option><option value="tenants">Tenants only</option></Select></Field>
        </div>
        <label className="flex items-center gap-3"><input type="checkbox" className="h-5 w-5" checked={pinned} onChange={(e) => setPinned(e.target.checked)} /> Pin to the top</label>
        <Button type="submit" size="lg" className="w-full" loading={create.isPending} disabled={title.trim().length < 3 || body.trim().length < 3}>Post and notify</Button>
      </form>
    </Sheet>
  );
}

// ───────────── settings ─────────────

export function SettingsScreen() {
  const toast = useToast();
  const q = useCommunity();
  const update = useUpdateCommunity();
  if (q.isLoading) return <Spinner />;
  if (q.error || !q.data) return <ErrorNote error={q.error} retry={() => q.refetch()} />;
  const s = q.data.settings;
  const save = (settings: Partial<typeof s>) => update.mutate({ settings }, { onError: (e) => toast.error(e), onSuccess: () => toast.success('Saved') });

  return (
    <div className="space-y-4">
      <PageHeader title="Settings" subtitle={q.data.name} />
      <Card className="space-y-4">
        <h2 className="font-semibold">Gate rules</h2>
        <Field label="How long to wait for a resident to answer" hint="After this, the guard is alerted that nobody responded.">
          <Select value={s.approvalTimeoutSec} onChange={(e) => save({ approvalTimeoutSec: Number(e.target.value) })}>
            {[30, 60, 90, 120, 180, 300].map((n) => <option key={n} value={n}>{n < 60 ? `${n} seconds` : `${n / 60} minute${n > 60 ? 's' : ''}`}</option>)}
          </Select>
        </Field>
        <Field label="If nobody answers" hint="Whether guards may let a visitor in without approval.">
          <Select value={s.noResponsePolicy} onChange={(e) => save({ noResponsePolicy: e.target.value as typeof s.noResponsePolicy })}>
            <option value="guard_discretion">Guard uses judgement (can call and admit)</option>
            <option value="deny">Never admit without approval</option>
          </Select>
        </Field>
      </Card>
      <Card className="space-y-2">
        <h2 className="font-semibold">Privacy</h2>
        <p className="text-sm text-slate-700">Visitor names and entry times are kept in the gate log, and complaint photos are kept with their complaint. Residents' numbers are used only to run this society's gate and helpdesk and are never sold or used for ads. Your society's data belongs to your society.</p>
      </Card>
    </div>
  );
}
